import { notification, Skeleton } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  apiPublicPaymentDetail,
  apiPublicPaymentPay,
  apiPublicSubscriberDetail,
  apiSubscriptionPlanDetails,
} from '../../../api/subscription';
import { formatDate, formatPrice } from '../../../utils/format';
import { searchPostalCode } from '../../../utils/searchPostalCode';
import { planAllowsMethod, paymentStatusMeta } from '../../../utils/subscriptions';
import {
  validateCardNumber,
  validateCEP,
  validateTaxpayerID,
} from '../../../utils/validatesCheckout';
import ExpiryDateSelector from '../../common/ExpiryDateSelector/ExpiryDateSelector';
import InputCheckout from '../../common/InputCheckout/InputCheckout';
import PublicStatusPage from '../../common/PublicStatusPage/PublicStatusPage';
import StateSelect from '../../common/StateSelect/StateSelect';
import './SubscriberPayment.scss';

const SubscriberPayment = () => {
  const { planId, subscriberId, paymentId } = useParams();
  const [planInfo, setPlanInfo] = useState(null);
  const [payment, setPayment] = useState(null);
  const [subscriber, setSubscriber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Payment form state
  const [paymentType, setPaymentType] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [useSavedCard, setUseSavedCard] = useState(false);
  const hasPopulatedFromURL = useRef(false);
  const [formData, setFormData] = useState({
    taxpayerId: '',
    ccNumber: '',
    ccName: '',
    ccCvv: '',
    addressCep: '',
    addressLine1: '',
    addressNumber: '',
    addressNeighborhood: '',
    addressCity: '',
    addressState: '',
    addressComplement: '',
  });

  const [errors, setErrors] = useState({});
  const [validFields, setValidFields] = useState({});
  const [selectedMonth, setSelectedMonth] = useState('01');
  const [selectedYear, setSelectedYear] = useState('25');
  const [postalCodeData, setPostalCodeData] = useState(null);

  // Result states
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [returnPix, setReturnPix] = useState(null);
  const [returnBoleto, setReturnBoleto] = useState(null);
  const [errorPurchase, setErrorPurchase] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [planData, paymentData] = await Promise.all([
        apiSubscriptionPlanDetails(planId),
        apiPublicPaymentDetail(subscriberId, paymentId),
      ]);
      setPlanInfo(planData);
      setPayment(paymentData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err);
    }

    try {
      const subscriberData = await apiPublicSubscriberDetail(subscriberId);
      setSubscriber(subscriberData);
    } catch {
      // Saved card feature is optional — page works without it
    }
  }, [planId, subscriberId, paymentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (postalCodeData && !postalCodeData?.erro) {
      setFormData((prevData) => ({
        ...prevData,
        addressLine1: postalCodeData?.logradouro || prevData.addressLine1,
        addressCity: postalCodeData?.localidade || prevData.addressCity,
        addressNeighborhood: postalCodeData?.bairro || prevData.addressNeighborhood,
        addressState: postalCodeData?.uf || prevData.addressState,
        addressCep: postalCodeData?.cep || prevData.addressCep,
      }));
      setValidFields((prev) => ({
        ...prev,
        addressLine1: true,
        addressCity: true,
        addressNeighborhood: true,
        addressState: true,
      }));
    }
  }, [postalCodeData]);

  const validate = useCallback(() => {
    const newErrors = {};

    if (paymentType === 'credit_card' && !useSavedCard) {
      const taxpayerError = validateTaxpayerID(formData?.taxpayerId);
      if (taxpayerError) newErrors.taxpayerId = taxpayerError;

      if (!formData.ccName) newErrors.ccName = 'Esse campo é obrigatório';
      if (!formData.ccCvv) newErrors.ccCvv = 'Esse campo é obrigatório';

      const ccNumberError = validateCardNumber(formData?.ccNumber);
      if (ccNumberError) newErrors.ccNumber = ccNumberError;
    }

    if (paymentType === 'boleto') {
      const taxpayerError = validateTaxpayerID(formData?.taxpayerId);
      if (taxpayerError) newErrors.taxpayerId = taxpayerError;

      const cepError = validateCEP(formData?.addressCep?.replace(/-/g, ''));
      if (cepError) newErrors.addressCep = cepError;

      if (!formData.addressLine1) newErrors.addressLine1 = 'Esse campo é obrigatório';
      if (!formData.addressNumber) newErrors.addressNumber = 'Esse campo é obrigatório';
      if (!formData.addressNeighborhood) newErrors.addressNeighborhood = 'Esse campo é obrigatório';
      if (!formData.addressCity) newErrors.addressCity = 'Esse campo é obrigatório';
      if (!formData.addressState) newErrors.addressState = 'Esse campo é obrigatório';
    }

    if (paymentType === 'pix') {
      const taxpayerError = validateTaxpayerID(formData?.taxpayerId);
      if (taxpayerError) newErrors.taxpayerId = taxpayerError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, paymentType, useSavedCard]);

  const handleSubmit = useCallback(
    (event) => {
      if (event) event.preventDefault();

      if (!paymentType) {
        notification.warning({
          message: 'Atenção',
          description: 'Selecione uma forma de pagamento',
          placement: 'topRight',
        });
        return;
      }

      if (validate()) {
        setIsDisabled(true);
        setFormLoading(true);

        let payload = { type: paymentType };

        if (paymentType === 'credit_card' && useSavedCard) {
          payload = {
            ...payload,
            use_saved_card: true,
          };
        } else if (paymentType === 'credit_card') {
          payload = {
            ...payload,
            taxpayer_id: formData.taxpayerId,
            installments: 1,
            card: {
              number: formData.ccNumber,
              cvv: formData.ccCvv,
              name: formData.ccName,
              expirationMonth: selectedMonth,
              expirationYear: `20${selectedYear}`,
            },
            save_card: saveCard,
          };
        }

        if (paymentType === 'boleto') {
          payload = {
            ...payload,
            taxpayer_id: formData.taxpayerId,
            address: {
              line1: formData.addressLine1,
              line2: formData.addressNumber,
              neighborhood: formData.addressNeighborhood,
              city: formData.addressCity,
              state: formData.addressState,
              postalCode: formData.addressCep?.replace(/-/g, ''),
            },
          };
        }

        if (paymentType === 'pix') {
          payload = {
            ...payload,
            taxpayer_id: formData.taxpayerId,
          };
        }

        apiPublicPaymentPay(subscriberId, paymentId, payload)
          .then((data) => {
            setFormLoading(false);

            if (data?.httpStatus === 402) {
              setErrorDetails(data?.error || { message_display: data?.message || 'Cartão recusado' });
              setErrorPurchase(true);
              return;
            }

            if (data?.httpStatus >= 400) {
              setErrorDetails(
                data?.errors || data?.error || { message_display: data?.message || 'Erro ao processar pagamento' }
              );
              setErrorPurchase(true);
              return;
            }

            if (data?.pix) {
              setReturnPix(data);
              return;
            }

            if (data?.boleto) {
              setReturnBoleto(data);
              return;
            }

            setPayment((prev) => ({ ...prev, payment_status: 'paid' }));
            setPaymentSuccess(data);
            notification.success({
              message: 'Sucesso',
              description: 'Pagamento realizado com sucesso!',
              placement: 'topRight',
            });
          })
          .catch(() => {
            setErrorDetails(null);
            setErrorPurchase(true);
            setFormLoading(false);
          });
      }
    },
    [validate, formData, paymentType, selectedMonth, selectedYear, subscriberId, paymentId, useSavedCard, saveCard]
  );

  const validateField = useCallback((name, value) => {
    let error = '';
    let isValid = true;

    if (name === 'ccName') {
      if (!value?.trim()) {
        error = 'Esse campo é obrigatório';
        isValid = false;
      }
    }

    if (name === 'taxpayerId') {
      error = validateTaxpayerID(value);
      isValid = error === '';
    }

    if (name === 'ccNumber') {
      error = validateCardNumber(value);
      isValid = error === '';
    }

    if (name === 'addressCep') {
      const rawCep = value?.replace(/-/g, '');
      error = validateCEP(rawCep);
      if (error === '') {
        setPostalCodeData(null);
        searchPostalCode(rawCep, setPostalCodeData);
      }
      isValid = error === '';
    }

    if (value?.trim() === '' && !error) {
      error = 'Este campo é obrigatório';
      isValid = false;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
    setValidFields((prevValid) => ({
      ...prevValid,
      [name]: isValid,
    }));
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      const nextValue = name === 'ccNumber' ? value.replace(/\D/g, '') : value;

      setFormData((prevData) => ({
        ...prevData,
        [name]: nextValue,
      }));

      validateField(name, nextValue);
    },
    [validateField]
  );

  const handleMonthChange = (e) => setSelectedMonth(e.target.value);
  const handleYearChange = (e) => setSelectedYear(e.target.value);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      notification.success({
        message: 'Sucesso',
        description: 'Código copiado com sucesso!',
        placement: 'topRight',
      });
    } catch {
      notification.error({
        message: 'Erro',
        description: 'Falha ao copiar o código.',
        placement: 'topRight',
      });
    }
  };

  const CircleCheckIcon = (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );

  const CircleAlertIcon = (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  );

  const CreditCardIcon = (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </svg>
  );

  const savedCard = subscriber?.saved_card;

  const handleUseSavedCard = (value) => {
    setUseSavedCard(value);
    if (value) {
      setErrors({});
    }
  };

  const PaymentSuccessPage = () => (
    <div className="PaymentSuccess">
      <div className="content">
        <CircleCheckIcon className="icon" />
        <h1 className="title">Pagamento Realizado!</h1>
        <p className="message">Seu pagamento foi processado com sucesso.</p>
        <div className="payment-info">
          <div className="plan-name">{payment?.plan_name || planInfo?.name}</div>
          <div className="total">{formatPrice(parseFloat(payment?.amount))}</div>
        </div>
      </div>
    </div>
  );

  const OrderUnsuccessful = ({ errorDetails }) => {
    const formatErrors = (errors) => {
      if (typeof errors === 'string') return errors;
      if (errors?.message_display) return errors.message_display;
      if (errors?.message) return errors.message;
      if (typeof errors === 'object') {
        return Object.entries(errors).map(([key, value]) => (
          <p key={key} className="error-message">
            {Array.isArray(value) ? value.join(', ') : value}
          </p>
        ));
      }
      return 'Erro desconhecido';
    };

    return (
      <div className="OrderUnsuccessful">
        <div className="content">
          <CircleAlertIcon className="icon" />
          <h1 className="title">Pagamento malsucedido</h1>
          {errorDetails && <div className="error-details">{formatErrors(errorDetails)}</div>}
          <p className="message">
            Lamentamos, mas houve um problema ao processar seu pagamento. Tente novamente ou entre em contato com o
            suporte ao cliente se o problema persistir.
          </p>
          <div className="actions">
            <button onClick={() => window.location.reload()} className="primary">
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  };

  const showForm = !errorPurchase && !paymentSuccess && !returnPix && !returnBoleto;
  const isPayable = payment?.is_payable;
  const isPaid = payment?.payment_status === 'paid';
  const subscriptionUrl = `/assinar/${planId}/${subscriberId}`;

  const statusBanner = () => {
    if (isPaid) {
      const amountText = formatPrice(parseFloat(payment?.amount));
      const dueDateText = formatDate(payment?.due_date);
      return {
        variant: 'success',
        heading: 'Pagamento já realizado',
        body: dueDateText
          ? `A cobrança de ${amountText} com vencimento em ${dueDateText} já foi paga.`
          : `A cobrança de ${amountText} já foi paga.`,
        action: { label: 'Ver minha assinatura', to: subscriptionUrl },
      };
    }

    if (!isPayable) {
      const { label: statusLabel } = paymentStatusMeta(payment?.payment_status);
      const hasStatusLabel = Boolean(statusLabel) && statusLabel !== payment?.payment_status;
      return {
        variant: 'warning',
        heading: 'Este pagamento não está mais disponível',
        body: hasStatusLabel
          ? `Esta cobrança está com o status "${statusLabel}" e não pode ser paga por este link.`
          : 'Esta cobrança não pode mais ser paga por este link.',
        action: { label: 'Ver minha assinatura', to: subscriptionUrl },
      };
    }

    return null;
  };

  if (error) {
    return error?.status === 404 ? (
      <PublicStatusPage
        className="SubscriberPayment"
        title="Pagamento de Assinatura"
        merchantName={planInfo?.account_name}
        variant="neutral"
        heading="Não encontramos este pagamento"
        body="O link pode estar incompleto ou a cobrança pode ter sido removida. Confira o endereço no e-mail que você recebeu."
        action={{ label: 'Ver minha assinatura', to: subscriptionUrl }}
      />
    ) : (
      <PublicStatusPage
        className="SubscriberPayment"
        title="Pagamento de Assinatura"
        merchantName={planInfo?.account_name}
        variant="warning"
        heading="Não conseguimos carregar este pagamento"
        body="Isso costuma ser temporário."
        action={{ label: 'Tentar novamente', onClick: fetchData }}
      />
    );
  }

  if (loading) {
    return (
      <PublicStatusPage
        className="SubscriberPayment"
        title="Pagamento de Assinatura"
        merchantName={planInfo?.account_name}
      >
        <Skeleton active />
      </PublicStatusPage>
    );
  }

  const banner = statusBanner();

  return (
    <PublicStatusPage
      className="SubscriberPayment"
      title="Pagamento de Assinatura"
      merchantName={planInfo?.account_name}
      variant={banner?.variant}
      heading={banner?.heading}
      body={banner?.body}
      action={banner?.action}
    >
      <div className="payment-details">
        <h3>Detalhes do Pagamento</h3>
        <div className="info-card">
          <div className="plan-name">{payment?.plan_name || planInfo?.name}</div>
          <div className="amount">{formatPrice(parseFloat(payment?.amount))}</div>
          <div className="info-row">
            <span className="label">Vencimento:</span>
            <span className="value">{formatDate(payment?.due_date)}</span>
          </div>
          <div className="info-row">
            <span className="label">Status:</span>
            <span className={`value status-badge ${paymentStatusMeta(payment?.payment_status).className}`}>
              {paymentStatusMeta(payment?.payment_status).label}
            </span>
          </div>
        </div>
      </div>

      {isPayable && showForm && (
        <div className="payment-form-container">
          <form id="payment-form" onSubmit={handleSubmit}>
            <div className="payment-type-container">
              <h3>Forma de Pagamento</h3>
              <div className="payment_buttons">
                {planAllowsMethod(planInfo?.payment_methods, 'CREDIT_CARD') && (
                  <button
                    type="button"
                    onClick={() => setPaymentType('credit_card')}
                    className={`payment_type_button ${paymentType === 'credit_card' ? 'active' : ''}`}
                  >
                    Cartão de Crédito
                  </button>
                )}
                {planAllowsMethod(planInfo?.payment_methods, 'BOLETO') && (
                  <button
                    type="button"
                    onClick={() => setPaymentType('boleto')}
                    className={`payment_type_button ${paymentType === 'boleto' ? 'active' : ''}`}
                  >
                    Boleto
                  </button>
                )}
                {planAllowsMethod(planInfo?.payment_methods, 'PIX') && (
                  <button
                    type="button"
                    onClick={() => setPaymentType('pix')}
                    className={`payment_type_button ${paymentType === 'pix' ? 'active' : ''}`}
                  >
                    PIX
                  </button>
                )}
              </div>
            </div>

            {paymentType === 'credit_card' && (
              <div className="payment_form">
                {savedCard && (
                  <div className="saved-card-option">
                    <div
                      className={`saved-card-card ${useSavedCard ? 'active' : ''}`}
                      onClick={() => handleUseSavedCard(true)}
                    >
                      <CreditCardIcon className="saved-card-icon" />
                      <div className="saved-card-details">
                        <span className="saved-card-brand">{savedCard.card_brand}</span>
                        <span className="saved-card-number">**** {savedCard.last4_digits}</span>
                      </div>
                      <span className="saved-card-holder">{savedCard.holder_name}</span>
                    </div>
                    <button
                      type="button"
                      className={`new-card-toggle ${!useSavedCard ? 'active' : ''}`}
                      onClick={() => handleUseSavedCard(false)}
                    >
                      Usar outro cartão
                    </button>
                  </div>
                )}
                {!useSavedCard && (
                  <>
                    <InputCheckout
                      disabled={isDisabled}
                      label="CPF ou CNPJ"
                      name="taxpayerId"
                      value={formData.taxpayerId}
                      onChange={handleChange}
                      errorMessage={errors.taxpayerId}
                      isValid={validFields.taxpayerId}
                    />
                    <InputCheckout
                      disabled={isDisabled}
                      label="Número do cartão"
                      name="ccNumber"
                      value={formData.ccNumber}
                      onChange={handleChange}
                      errorMessage={errors.ccNumber}
                      isValid={validFields.ccNumber}
                    />
                    <div className="row">
                      <ExpiryDateSelector
                        selectedMonth={selectedMonth}
                        selectedYear={selectedYear}
                        onMonthChange={handleMonthChange}
                        onYearChange={handleYearChange}
                      />
                      <InputCheckout
                        disabled={isDisabled}
                        label="CVV"
                        name="ccCvv"
                        className="small"
                        value={formData.ccCvv}
                        maxLength={4}
                        onChange={handleChange}
                        errorMessage={errors.ccCvv}
                        isValid={validFields.ccCvv}
                      />
                    </div>
                    <InputCheckout
                      disabled={isDisabled}
                      label="Nome do Portador"
                      name="ccName"
                      value={formData.ccName}
                      onChange={handleChange}
                      errorMessage={errors.ccName}
                      isValid={validFields.ccName}
                    />
                    <label className="save-card-checkbox">
                      <input
                        type="checkbox"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        disabled={isDisabled}
                      />
                      <span>Salvar cartão para cobranças futuras</span>
                    </label>
                  </>
                )}
              </div>
            )}

            {paymentType === 'boleto' && (
              <div className="payment_form">
                <div className="payment__redirect">
                  O Banco solicita algumas informações extras para a geração do boleto.
                </div>
                <InputCheckout
                  disabled={isDisabled}
                  label="CPF ou CNPJ"
                  name="taxpayerId"
                  value={formData.taxpayerId}
                  onChange={handleChange}
                  errorMessage={errors.taxpayerId}
                  isValid={validFields.taxpayerId}
                />
                <InputCheckout
                  disabled={isDisabled}
                  label="CEP"
                  name="addressCep"
                  value={formData.addressCep}
                  onChange={handleChange}
                  errorMessage={errors.addressCep}
                  isValid={validFields.addressCep}
                  maxLength={9}
                />
                <InputCheckout
                  disabled={isDisabled}
                  label="Endereço"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  errorMessage={errors.addressLine1}
                  isValid={validFields.addressLine1}
                />
                <div className="row">
                  <InputCheckout
                    disabled={isDisabled}
                    label="Número"
                    name="addressNumber"
                    className="small"
                    value={formData.addressNumber}
                    onChange={handleChange}
                    errorMessage={errors.addressNumber}
                    isValid={validFields.addressNumber}
                  />
                  <InputCheckout
                    disabled={isDisabled}
                    label="Complemento"
                    name="addressComplement"
                    className="small"
                    value={formData.addressComplement}
                    onChange={handleChange}
                    isValid={formData.addressComplement ? true : ''}
                  />
                </div>
                <InputCheckout
                  disabled={isDisabled}
                  label="Bairro"
                  name="addressNeighborhood"
                  value={formData.addressNeighborhood}
                  onChange={handleChange}
                  errorMessage={errors.addressNeighborhood}
                  isValid={validFields.addressNeighborhood}
                />
                <div className="row">
                  <InputCheckout
                    disabled={isDisabled}
                    label="Cidade"
                    name="addressCity"
                    className="small"
                    value={formData.addressCity}
                    onChange={handleChange}
                    errorMessage={errors.addressCity}
                    isValid={validFields.addressCity}
                  />
                  <StateSelect
                    label="Estado"
                    name="addressState"
                    value={formData.addressState}
                    onChange={handleChange}
                    errorMessage={errors.addressState}
                    isValid={validFields.addressState}
                  />
                </div>
              </div>
            )}

            {paymentType === 'pix' && (
              <div className="payment_form">
                <div className="payment__redirect">
                  Ao clicar em "Pagar" você será redirecionado para o QRCode do PIX.
                </div>
                <InputCheckout
                  disabled={isDisabled}
                  label="CPF ou CNPJ"
                  name="taxpayerId"
                  value={formData.taxpayerId}
                  onChange={handleChange}
                  errorMessage={errors.taxpayerId}
                  isValid={validFields.taxpayerId}
                />
              </div>
            )}

            {paymentType && (
              <div className="payment-container">
                {formLoading ? (
                  <div className="loading-div">
                    <span className="loader-text">Processando...</span>
                    <span className="loader"></span>
                  </div>
                ) : (
                  <button type="submit">PAGAR {formatPrice(parseFloat(payment?.amount))}</button>
                )}
              </div>
            )}
          </form>
        </div>
      )}

      {returnPix && (
        <div className="pix-feedback">
          <h2>Seu QRCode está pronto!</h2>
          <img src={returnPix.pix.qr_code} alt="QR Code" width={250} />
          <h3>Pix Copia e Cola</h3>
          <div className="pix-copy-paste">
            <div className="pix-code">{returnPix.pix.copy_paste}</div>
            <button onClick={() => handleCopy(returnPix.pix.copy_paste)}>Copiar</button>
          </div>
          <p className="pix-message">Após o pagamento, sua cobrança será atualizada automaticamente.</p>
        </div>
      )}

      {returnBoleto && (
        <div className="boleto-feedback">
          <h2>Visualizar Boleto</h2>
          <p className="boleto-message">Após o pagamento, sua cobrança será atualizada automaticamente.</p>
          <div>
            <button onClick={() => window.open(returnBoleto?.boleto?.url, '_blank')}>
              Visualizar página
            </button>
          </div>
          <iframe
            src={returnBoleto?.boleto?.url}
            width="100%"
            height="600px"
            title="Boleto"
            style={{ border: 'none', marginTop: '24px' }}
          />
        </div>
      )}

      {paymentSuccess && <PaymentSuccessPage />}
      {errorPurchase && <OrderUnsuccessful errorDetails={errorDetails} />}
    </PublicStatusPage>
  );
};

export default SubscriberPayment;
